import { useEffect, useState } from 'react';
import { FREE_LIMIT, quotaExceededMessage, type QuotaStatus } from '@tarot/shared';
import { useAdReward, useQuota } from './useQuota';
import { canShowAd, playRewardedAd } from './ad';

export interface QuotaBlock {
  message: string;
  status?: QuotaStatus;
  /** 'ad' = 광고를 보면 계속 가능 / 'quota' = 하루 상한 도달 */
  reason?: 'quota' | 'ad';
}

/**
 * 이용 횟수 안내 (정책은 shared quota.ts).
 *  - 무료 구간: 표시 없음 (잔여 횟수를 미리 보여 주면 유료 전환처럼 읽힌다)
 *  - 광고 구간: "광고 한 편을 보면 한 번 더 볼 수 있어요" — 무료분 뒤로는 매번 뜬다
 *  - 하루 상한: 안내만. 로그인해도 횟수가 같으므로 로그인을 권하지 않는다
 */
export function QuotaGate({
  block,
  onAdWatched,
}: {
  block?: QuotaBlock | null;
  /** 광고 시청 완료 후 이어서 진행할 동작 */
  onAdWatched?: () => void;
}) {
  const { quota } = useQuota();

  /**
   * 하루 상한 도달 — 서버 응답이 없어도 미리 감지.
   * 서버가 막은 경우(block)는 **그 메시지를 그대로** 쓴다. 예전에는 여기서 `true`만 남아
   * 문구가 빈 채로 떴다.
   */
  const exhausted: QuotaBlock | null =
    block && block.reason !== 'ad'
      ? block
      : quota && quota.remaining <= 0
        ? { message: quotaExceededMessage(quota), status: quota }
        : null;

  if (exhausted) {
    return (
      <section className="flex flex-col items-center gap-3 rounded-3xl border border-gold/25 bg-gold/5 p-5 text-center">
        <p className="text-sm leading-relaxed text-moon">{exhausted.message}</p>
        {/* 하루 횟수 문제일 때만 — 분당 폭주 차단(429, 상태 없음)은 자정과 무관하다 */}
        {exhausted.status && (
          <p className="text-xs text-moon-dim/70">매일 자정(한국시간)에 초기화돼요</p>
        )}
      </section>
    );
  }

  // 광고를 봐야 하는 시도
  if (block?.reason === 'ad' || quota?.needsAd) {
    return <AdGate onDone={onAdWatched} />;
  }

  // 평소에는 아무것도 띄우지 않는다. "오늘 무료 N/M회 남음"처럼 잔여 횟수를 미리 보여 주면
  // 무료분을 다 쓰면 유료로 넘어간다는 인상을 준다(실제로는 광고만 보면 계속 무료).
  // 정말 막힐 때(상한·광고)만 위쪽 분기에서 안내한다.
  return null;
}

/** 광고 시청 안내 — 보고 나면 이어서 진행 */
function AdGate({ onDone }: { onDone?: () => void }) {
  const ad = useAdReward();
  const [playing, setPlaying] = useState(false);
  /** 광고를 끝까지 못 본 경우 — 토스 미니앱에서만 생긴다 (ad.ts 정책) */
  const [notEarned, setNotEarned] = useState(false);
  /**
   * 지금 광고를 실제로 띄울 수 있는가. 못 띄우면 **광고 이야기를 하지 않는다** —
   * "광고를 보면"이라 해놓고 아무것도 안 뜨면 그건 거짓말이다.
   * 판정 전에는 광고 문구를 쓰지 않는 쪽(false)에서 시작한다.
   */
  const [adReady, setAdReady] = useState(false);
  useEffect(() => {
    let alive = true;
    void canShowAd().then((ok) => alive && setAdReady(ok));
    return () => {
      alive = false;
    };
  }, []);

  /**
   * 광고를 틀고, 끝나면 보상을 받는다.
   * 웹에서는 광고 쪽 오류를 playRewardedAd가 삼켜 항상 시청으로 인정한다 (ad.ts 정책) —
   * 여기서 남는 실패는 우리 서버 문제뿐이다.
   * 토스 미니앱은 `userEarnedReward` 없이는 지급할 수 없어(정책) false가 올 수 있다.
   */
  const watch = async () => {
    if (playing || ad.isPending) return;
    setPlaying(true);
    setNotEarned(false);
    const earned = await playRewardedAd();
    setPlaying(false);
    if (!earned) {
      setNotEarned(true);
      return;
    }
    ad.mutate(undefined, { onSuccess: () => onDone?.() });
  };

  const busy = playing || ad.isPending;
  return (
    <section className="flex flex-col items-center gap-2.5 rounded-2xl border border-gold/30 bg-gold/5 p-4 text-center">
      <p className="text-sm text-moon">
        {adReady ? (
          <>
            오늘 무료 {FREE_LIMIT}회를 보셨어요. <b className="text-gold">광고 한 편</b>을 보면 한
            번 더 볼 수 있어요.
          </>
        ) : (
          <>
            오늘 무료 {FREE_LIMIT}회를 보셨어요. <b className="text-gold">이어서</b> 보실 수 있어요.
          </>
        )}
      </p>
      <button
        onClick={watch}
        disabled={busy}
        className="flex h-12 w-full max-w-xs items-center justify-center rounded-2xl bg-gold px-5 text-sm font-semibold text-night transition hover:bg-gold-deep disabled:opacity-50"
      >
        {busy
          ? adReady
            ? '광고 불러오는 중…'
            : '준비 중…'
          : adReady
            ? '광고 보고 이어서 보기'
            : '이어서 보기'}
      </button>
      {notEarned && (
        <p className="text-xs text-red-300">
          광고를 끝까지 보셔야 이어서 볼 수 있어요. 다시 눌러 주세요.
        </p>
      )}
      {ad.isError && (
        <p className="text-xs text-red-300">
          횟수를 올리지 못했어요. 잠시 후 다시 눌러 주세요 — 광고는 다시 보지 않아도 돼요.
        </p>
      )}
    </section>
  );
}

/**
 * 지금 이 자리에서 진행할 수 없는 상태 — 시작 버튼을 미리 비활성화할 때 쓴다.
 *
 * 하루 상한 도달뿐 아니라 **광고를 봐야 하는 상태도 포함**한다.
 * 광고를 막지 않던 때는 주제만 고르고 통과한 뒤 카드를 다 뽑고 나서야 해석 단계에서
 * 402로 막혀, 사용자가 헛수고를 했다 (2026-08-20 실사용 확인).
 * 함께 렌더되는 QuotaGate가 바로 위에서 광고 시청 버튼을 제공하므로, 막아도 길이 막히지 않는다.
 */
export function useQuotaBlocked(block?: QuotaBlock | null): boolean {
  const { quota } = useQuota();
  if (block) return true; // 상한이든 광고든, 서버가 막았으면 여기서 멈춘다
  if (!quota) return false; // 아직 모르면 막지 않는다 (첫 로딩에 버튼이 죽어 보이면 안 됨)
  return quota.remaining <= 0 || quota.needsAd;
}
